import React, {useEffect, useRef, useState} from 'react';
import { useSelector } from 'umi';
import { Layout, Card, Justify, SearchBox, Button, Row, Col } from 'tea-component';
import AbnormalPage from '@/pages/datalab/components/AbnormalPage';
import ChartTable from './components/ChartTable';
import { getChartLabels } from '@dbl/services/chart';
import Category, {CategoryRef} from '../../components/Category';
import {useDblIntl} from "@/pages/datalab/utils/utils";
import {buriedPoint} from "@/pages/home/utils/buriedPoint";
import CreateChart from '../WorkBench/components/CreateChart';
import { AuthPermissionDisablesContainers } from '../WorkBench/MyDashboard';
import './index.scss';
import { DATALAB_EDITOR } from '@dbl/core/global';
import { useUrlParam } from "@dbl/core/global";
import { IChartTableRef, IChartTagCategoryItemDto, IGetChartLabelsSubLabelDto, IGetChartListSubLabelRequest } from './types';

const { Content } = Layout;

function MyAnalysis() {
  const { gameCode } = useSelector((state) => ({gameCode: state.datalab.gameCode}));
  const chartTableRef = useRef<IChartTableRef>(null);
  const categoryRef = useRef<CategoryRef>(null);
  
  useEffect(() => {
    buriedPoint({
      pageId: '600101',
      pageName: '我的图表',
      extInfo: `{"pageName": "My Chart", "pageNameCn": "我的图表", "id": ""}`,
      pagePath: '/WorkBench/MyAnalysis',
      uid: gameCode,
      typeId: "datalab_pc_v2",
      menuId: '0',
    });
  }, [gameCode]);

  // 搜索框点击搜索按钮后, 触发表格请求的搜索字段
  const [searchWords, setSearchWords] = useUrlParam<string | undefined>({
    urlKey: 'searchWords',
    defaultValue: '',
  });
  // 搜索框受控value
  const [searchValue, setSearchValue] = useState<string | undefined>(searchWords ?? '');
  const [responseLabels, setResponseLabels] = useUrlParam<{allSelected: boolean; subLabels: IGetChartListSubLabelRequest[];}>({
    urlKey: 'selectedLabels',
    defaultValue: {allSelected: true, subLabels: []},
  }); // 上传sub_labels
  
  // 任务数据类型定义
  interface ITasksData {
    labels?: IChartTagCategoryItemDto[];
    sub_labels?: IGetChartLabelsSubLabelDto[];
  }
  
  const [tasks, setTasks] = useState<ITasksData>({}); // 下传labels

  const [pageStatus, setPageStatus] = useState('loading');
  const [showAddChartModal, setShowAddChartModal] = useState(false);

  const { dblFormatMessage, isEn } = useDblIntl('myAnalysis');

  useEffect(() => {
    setPageStatus('loading');
    requestChartLabels();
  }, [gameCode]);

  const requestChartLabels = () => {
    // 任务权限和任务标签，接口，本次只用到标签
    getChartLabels({ game_code: gameCode })
      .then((res) => {
        setTasks(res || {});
        setPageStatus('normal');
      })
      .catch((e) => {
        setPageStatus('error');
        console.log('任务权限和标签请求错误: ', e);
      });
  };

  return (
    <Content>
      <Content.Header title={dblFormatMessage("title")} />
      <Content.Body full className="dbl-my-analyze">
        <Row>
          <Col>
            <Justify
              className="justify_search"
              left={
                <AuthPermissionDisablesContainers
                  type={DATALAB_EDITOR.AddChartBtn}
                  showNoPermissionBubble
                >
                  <Button
                    type="primary"
                    onClick={() => {
                      setShowAddChartModal(true);
                    }}
                  >
                    {dblFormatMessage("new")}
                  </Button>
                </AuthPermissionDisablesContainers>
              }
              right={
                <SearchBox
                  className="analyze-search"
                  value={searchValue}
                  onChange={(value) => {
                    setSearchValue(value);
                    if (value.length === 0) {
                      setSearchWords('');
                      setResponseLabels({subLabels: [], allSelected: true});
                      chartTableRef.current?.searchChartList('');
                      chartTableRef.current?.resetState();
                      categoryRef.current?.resetState();
                    }
                  }}
                  onSearch={(keyword) => {
                    setSearchWords(keyword);
                    if (keyword !== searchWords) {
                      setResponseLabels({subLabels: [], allSelected: true});
                      chartTableRef.current?.searchChartList(keyword ?? '');
                      chartTableRef.current?.resetState();
                      categoryRef.current?.resetState();
                    }
                  }}
                  onClear={() => {
                    setSearchWords('');
                  }}
                  placeholder={dblFormatMessage("chart_search_placeholder")}
                />
              }
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Card>
              <Card.Body style={{ paddingTop: '0px' }}>
                <AbnormalPage status={pageStatus}>
                  <Category
                    ref={categoryRef}
                    labelsHeadText={dblFormatMessage("task_permissions")}
                    subLabelsHeadText={dblFormatMessage("task_tags")}
                    isShowLabels={false}
                    isShowExpand={true}
                    labels={tasks.labels}
                    subLabels={tasks.sub_labels}
                    onChange={(value: {allSelected: boolean; subLabels: IGetChartListSubLabelRequest[];}) => {
                      chartTableRef.current?.categoryFilterChartList(value);
                      setResponseLabels(value);
                    }}
                    isEn={isEn}
                  />
                  {
                    // 这个判断是为了避免进入页面时发起两次表格数据请求.
                    responseLabels !== undefined ? (
                      <ChartTable
                        ref={chartTableRef}
                        gameCode={gameCode}
                        searchWords={searchWords}
                        clearSearchWords={() => {
                          setSearchWords('');
                          setSearchValue('');
                        }}
                        labels={tasks.labels}
                        subLabels={responseLabels?.subLabels}
                        allSelected={responseLabels?.allSelected}
                        requestChartLabels={requestChartLabels}
                      />
                    ) : (
                      ''
                    )
                  }
                </AbnormalPage>
                <CreateChart visible={showAddChartModal} gameCode={gameCode} close={() => setShowAddChartModal(!showAddChartModal)} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Content.Body>
    </Content>
  );
}
export default MyAnalysis;
